import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Bell,
  Check,
  MessageSquare,
  FileText,
  Award,
  X,
  Loader2,
  Layers,
  PlayCircle,
  CheckCircle,
} from 'lucide-react';

const NotificationCenter = () => {
  const { token, isStudent, isTeacher, isApprovedStudent } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const prevUnreadRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const role = isTeacher ? 'teacher' : isStudent ? 'student' : null;
  const listUrl = role === 'teacher' ? `${API_URL}/api/teacher/notifications` : `${API_URL}/api/student/notifications`;
  const unreadUrl = role === 'teacher' ? `${API_URL}/api/teacher/notifications/unread-count` : `${API_URL}/api/student/notifications/unread-count`;
  const markReadUrl = (id) => role === 'teacher' ? `${API_URL}/api/teacher/notifications/${id}/read` : `${API_URL}/api/student/notifications/${id}/read`;
  const markAllReadUrl = role === 'teacher' ? `${API_URL}/api/teacher/notifications/read-all` : null;

  const playSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!token || !role) return;
    setLoading(true);
    try {
      const response = await fetch(listUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
    setLoading(false);
  }, [token, role, listUrl]);

  const fetchUnreadCount = useCallback(async () => {
    if (!token || !role) return;
    try {
      const response = await fetch(unreadUrl, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const newCount = data.count;
        if (prevUnreadRef.current !== null && newCount > prevUnreadRef.current) {
          playSound();
          // Fetch new notifications to find the latest one for the toast
          const listRes = await fetch(listUrl, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (listRes.ok) {
            const notifs = await listRes.json();
            setNotifications(notifs);
            // Show toast for the newest unread notification
            const newest = notifs.find(n => !n.is_read);
            if (newest) {
              toast(newest.title, {
                description: newest.message,
                duration: 5000,
              });
            }
          }
        }
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token, role, unreadUrl, listUrl, playSound]);

  useEffect(() => {
    if (!role) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, role]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(markReadUrl(notificationId), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (markAllReadUrl) {
      try {
        await fetch(markAllReadUrl, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
        return;
      } catch (e) {
        console.error('mark all read failed', e);
      }
    }
    // Fallback: per-item mark for student or if endpoint failed
    const unread = notifications.filter(n => !n.is_read);
    for (const n of unread) {
      await markAsRead(n.id);
    }
  };

  const getNotificationLink = (n) => {
    const meta = n?.metadata || {};
    const t = meta.type || '';
    if (isStudent) {
      // Students may ONLY open a notification that points to their own result
      // (i.e. the mark). Exam / question-bank "created" notifications and any
      // other type are informational only — they must NOT navigate anywhere,
      // so they never expose exam lists, the question bank, or other data.
      // A question-bank file that is now LOCKED closes its link entirely (the
      // backend tags it), so an old notification can't re-open a locked bank.
      if (meta.topic_id && meta.locked) return null;
      if (t === 'grade_released') {
        if (meta.topic_id && meta.attempt_id) return `/student/topic-attempts/${meta.attempt_id}/results`;
        if (meta.exam_id && meta.session_id) return `/student/exam/${meta.exam_id}/results/${meta.session_id}`;
        return null;
      }
      if (t === 'grade_visibility_changed' && meta.exam_id && meta.session_id) {
        return `/student/exam/${meta.exam_id}/results/${meta.session_id}`;
      }
      if (t === 'exam_submitted' && meta.exam_id && meta.session_id) {
        return `/student/exam/${meta.exam_id}/results/${meta.session_id}`;
      }
      // Everything else (exam_created, topic_created, video_created, …): no link.
      return null;
    }
    if (isTeacher) {
      if ((t === 'exam_started' || t === 'exam_completed' || t === 'exam_submitted') && meta.exam_id) {
        return `/teacher/exams/${meta.exam_id}/monitor`;
      }
      if ((t === 'topic_started' || t === 'topic_reattempted' || t === 'topic_completed') && meta.student_id) {
        return `/teacher/students/${meta.student_id}/attempts`;
      }
      if (t === 'grade_released' && meta.exam_id) {
        return `/teacher/exams/${meta.exam_id}/analytics`;
      }
      if (t === 'student_registered') {
        return '/teacher/dashboard?tab=students';
      }
      if (t === 'violation_reported' && meta.exam_id) {
        return `/teacher/exams/${meta.exam_id}/monitor`;
      }
    }
    return null;
  };

  const getNotificationIcon = (n) => {
    const t = n?.metadata?.type || '';
    if (t === 'grade_released' || t === 'grade_visibility_changed' || (n?.title || '').includes('درجة') || (n?.title || '').includes('تقييم')) {
      return <Award className="w-4 h-4 text-amber-600" />;
    }
    if (t === 'topic_created' || t === 'topic_started' || t === 'topic_completed' || t === 'topic_reattempted') {
      return <Layers className="w-4 h-4 text-purple-600" />;
    }
    if (t === 'exam_started') return <PlayCircle className="w-4 h-4 text-blue-600" />;
    if (t === 'exam_completed') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (t === 'exam_created' || (n?.title || '').includes('اختبار')) {
      return <FileText className="w-4 h-4 text-blue-600" />;
    }
    return <MessageSquare className="w-4 h-4 text-emerald-600" />;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    return date.toLocaleDateString('ar-SA');
  };

  if (!role) return null;

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">الإشعارات</h3>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center">
                  <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">لا توجد إشعارات</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notification) => {
                    const link = getNotificationLink(notification);
                    return (
                    <div
                      key={notification.id}
                      className={`p-4 border-b border-slate-50 transition-colors ${
                        link ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'
                      } ${!notification.is_read ? 'bg-blue-50/50' : ''}`}
                      onClick={async () => {
                        // A disabled/not-approved student must not be able to re-enter
                        // results/exams through a notification link.
                        if (isStudent && !isApprovedStudent) {
                          if (link) toast.error('حسابك غير مفعّل — لا يمكنك فتح هذا الرابط');
                          if (!notification.is_read) await markAsRead(notification.id);
                          return;
                        }
                        if (!notification.is_read) await markAsRead(notification.id);
                        // Only navigate when the notification points to a result (the mark).
                        if (link) { setIsOpen(false); navigate(link); }
                      }}
                      data-testid={`notification-${notification.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                          {getNotificationIcon(notification)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${!notification.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0" />}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{notification.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatTime(notification.created_at)}</p>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="p-3 border-t border-slate-100 bg-slate-50">
                <Button variant="ghost" size="sm" className="w-full text-slate-600" onClick={markAllAsRead}>
                  <Check className="w-4 h-4 ml-2" />
                  تحديد الكل كمقروء
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
